import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, Mail, ArrowLeft, CheckCircle, Shield, Fingerprint } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useWebAuthn } from '@/hooks/useWebAuthn';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api/marketplace';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // T&C re-acceptance state
  const [showTcModal, setShowTcModal] = useState(false);
  const [tcTerms, setTcTerms] = useState(null);
  const [tcAccepting, setTcAccepting] = useState(false);

  // Biometric login state
  const { supported: webauthnSupported, loading: bioLoading, checkAvailable, authenticate } = useWebAuthn();
  const [biometricAvailable, setBiometricAvailable] = useState(false);

  // Check biometric availability when email changes
  const checkBiometric = useCallback(async (email) => {
    if (!webauthnSupported || !email || !email.includes('@')) {
      setBiometricAvailable(false);
      return;
    }
    const available = await checkAvailable(email);
    setBiometricAvailable(available);
  }, [webauthnSupported, checkAvailable]);

  useEffect(() => {
    const timer = setTimeout(() => checkBiometric(formData.email), 500);
    return () => clearTimeout(timer);
  }, [formData.email, checkBiometric]);

  const handleBiometricLogin = async () => {
    if (!formData.email) {
      toast.error('Please enter your email first');
      return;
    }
    const result = await authenticate(formData.email);
    if (result.success) {
      localStorage.setItem('token', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
      toast.success('Biometric login successful!');
      navigate('/marketplace/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Check if T&C re-acceptance is required
      if (result.tc_acceptance_required && result.tc_terms) {
        setTcTerms(result.tc_terms);
        setShowTcModal(true);
        setLoading(false);
        return;
      }
      toast.success('Login successful!');
      navigate('/marketplace/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };
  
  const handleAcceptTC = async () => {
    if (!tcTerms) return;
    setTcAccepting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tc/accept`, {
        terms_id: tcTerms.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowTcModal(false);
      toast.success('Terms accepted. Welcome back!');
      navigate('/marketplace/dashboard');
    } catch (err) {
      toast.error('Failed to record acceptance. Please try again.');
    } finally {
      setTcAccepting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setResetSent(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      // Show success even if email doesn't exist (security best practice)
      setResetSent(true);
      toast.success('If this email exists, you will receive reset instructions');
    }
    setForgotLoading(false);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setResetSent(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex flex-col items-center justify-center mb-4 gap-2">
            <img src="/logo-icon.png" alt="Brand Nexus" className="h-[72px] w-[72px] object-contain" />
            <span className="font-extrabold text-[22px] text-slate-900 tracking-[2px]">BRAND NEXUS</span>
          </div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to your account to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your.email@company.com"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Biometric Login */}
          {biometricAvailable && (
            <div className="mt-3">
              <div className="relative flex items-center justify-center my-2">
                <div className="border-t border-slate-200 flex-1" />
                <span className="px-3 text-xs text-slate-400 uppercase">or</span>
                <div className="border-t border-slate-200 flex-1" />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={bioLoading}
                onClick={handleBiometricLogin}
                data-testid="biometric-login-btn"
                className="w-full border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-all"
              >
                {bioLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-4 h-4 mr-2 text-blue-600" />
                    Sign in with Face ID / Biometrics
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="mt-4 text-center text-sm space-y-2">
            <span className="text-slate-600">Don't have an account?</span>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/marketplace/register?type=brand_manager" className="text-blue-600 hover:underline font-medium">
                Register as Brand Manager
              </Link>
              <span className="text-slate-400">|</span>
              <Link to="/marketplace/register?type=vendor" className="text-orange-600 hover:underline font-medium">
                Register as Vendor
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resetSent ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Check Your Email
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 text-blue-600" />
                  Reset Password
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {resetSent
                ? "We've sent password reset instructions to your email address."
                : "Enter your email address and we'll send you instructions to reset your password."
              }
            </DialogDescription>
          </DialogHeader>

          {resetSent ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 text-sm">
                  If an account exists for <strong>{forgotEmail}</strong>, you will receive an email with reset instructions.
                </p>
              </div>
              <div className="text-center text-sm text-slate-500">
                <p>Didn't receive the email? Check your spam folder or</p>
                <button
                  onClick={() => setResetSent(false)}
                  className="text-blue-600 hover:underline mt-1"
                >
                  try again with a different email
                </button>
              </div>
              <Button
                onClick={closeForgotPassword}
                className="w-full"
                variant="outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotPassword}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="send-reset-btn"
                >
                  {forgotLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* T&C Re-acceptance Modal */}
      <Dialog open={showTcModal} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Updated Terms & Conditions
              {tcTerms?.version && <Badge variant="outline" className="text-[10px]">v{tcTerms.version}</Badge>}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Our Terms & Conditions have been updated. Please review and accept to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 bg-slate-50 p-4 rounded-lg border max-h-[50vh] overflow-y-auto" data-testid="login-tc-content">
            {tcTerms?.content || 'Loading...'}
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowTcModal(false);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                toast.info('You must accept the updated Terms & Conditions to continue');
              }}
              data-testid="tc-decline-btn"
            >
              Decline & Logout
            </Button>
            <Button
              onClick={handleAcceptTC}
              disabled={tcAccepting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="tc-accept-login-btn"
            >
              {tcAccepting ? 'Accepting...' : 'I Accept & Continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
